import {Grid, makeStyles, Paper, Button, Typography } from '@material-ui/core';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { COLORS } from '../../../constants/colors';
import { PROJECT } from '../../../constants/props';
import Input from '../../common/input';
import Searcher from '../../skills/searcher';
import Skill from '../../skills/skill';
import useNotifications from '../../../hooks/useNotification';
import { updateProject, postProjectSkill, deleteProjectSkill, saveProject } from '../../../api/project';
import { debounce } from 'lodash';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makeStyles({
  root: {
    //marginLeft: '5.5625rem',
    marginLeft: 0,
    '&:not(:last-of-type)': {
      marginBottom: '1.6875rem',
    },
    paddingBottom: '1.25rem',
    '&:last-of-type': {
      paddingBottom: '.75rem',
    },
    borderBottom: `2px solid  ${COLORS.primary.main}`,
  },
  title: {
    marginBottom: '.75rem',
    fontSize: '1.25rem',
    lineHeight: '150%',
    textTransform: 'uppercase',
  },
  skill: {
    marginRight: '.625rem',
    marginBottom: '1rem',
  },
  collapse: {
    marginBottom: '2rem',
  },
  searcher: {
    width: '20rem',
    marginBottom: '1.875rem',
  },
  project: {
    fontSize: '1.25rem',
    lineHeight: '150%',
    marginBottom:10,
    marginTop:10,
    textTransform: 'uppercase',
  },
  skills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.625rem',
  },
  more: {
    height: '1.5rem',
    padding: '0 .5rem',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  input: {
    '& textarea:disabled': {
      background: '#a7a7a7',
    },
    '& textarea': {
      margin: '0 !important',
      padding: '1.0625rem 1.25rem',
    },
    '& input:disabled': {
      background: '#a7a7a7',
    },
    '& input': {
      margin: '0 !important',
      padding: '1.0625rem 1.25rem',
    },
  }
});

const Edit = ({ hash, role, roleHash, name: initialName, description: initialDescription, 
  skills, projectIdx, onSubmit, onDelete, appendProject, experienceHash,reloadCandidateUpdatedAtData }) => {
  const classes = useStyles();
  const notifications = useNotifications();
  const lastRole = useRef(role);
  const lastRoleHash = useRef(roleHash);
  const lastName  = useRef(initialName);
  const lastDescription = useRef(initialDescription);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [projectHash, setProjectHash] = useState(hash);
  const [loading, setLoading] = useState(false)

  const sendProjectUpdate = () => {

    setLoading(true)

    if(projectHash && name && description && role && roleHash && (lastName.current !== name || lastRole.current !== role || lastDescription.current !== description) ){
      updateProject(projectHash, { immediately: true, role, roleHash, name, description  }).then((res) => {
        if (res?.success) {
          lastRole.current = role;
          lastName.current = name;
          lastRoleHash.current = roleHash;
          lastDescription.current = description;
          notifications.success({ message: 'Project was successfully updated', });
        } else {
          notifications.error(res.error);
        }
        setLoading(false)
      });
    } else if (name && description && role && roleHash && !projectHash) {
      saveProject({ immediately: true, name, description, role, roleHash, skills, experienceHash })
      .then((res) => {
        console.log(res)
        if(res.success){
          lastRole.current = role;
          lastName.current = name;
          lastRoleHash.current = roleHash;
          lastDescription.current = description;
          setProjectHash(res.data.hash);
          reloadCandidateUpdatedAtData()
          notifications.success({
            message: 'Project was successfully saved',
          });
        } else {
          notifications.error(res.error);
        }
        setLoading(false)
      })
    } else {
      setLoading(false)
    }
  }

  const handleSubmit = useCallback(()=>sendProjectUpdate(), [role, roleHash, name, description, projectHash]);

  useEffect(() => {
    if(lastRole.current !== role) handleSubmit();
  }, [role])

  function handleSelect({ hash: skill }) {
    const tempHash = hash || projectHash;
    postProjectSkill(tempHash, { skill, immediately: true }).then((res) => {
      if (res.success) {
        reloadCandidateUpdatedAtData()
        onSubmit()
      } else {
        notifications.error(res.error);
      }
    });
  }

  function handleDelete({ hash: skill }) {
    const tempHash = hash || projectHash;
    deleteProjectSkill(tempHash, { skill, immediately: true }).then((res) => {
      if (res.success) {
        reloadCandidateUpdatedAtData()
        onSubmit()
      } else {
        notifications.error(res.error);
      }
    });
  }
  
  return (
    <>
    <Grid >
      <Typography className={classes.project} component="h4">
        Project
      </Typography>
    </Grid>
    <Grid container className={classes.root} direction="row" spacing={5}>
      <Grid container item direction="row" spacing={2}>
        <Grid item>
          <Button variant="contained" color="primary" onClick={() => appendProject(projectIdx)} disabled={loading}>
              <FontAwesomeIcon icon={faPlus} />
          </Button>
        </Grid>
        <Grid item>
          <Input
              className={classes.input}
              variant="h1"
              rows={6}
              value={name}
              onChange={({ target }) => setName(target.value)}
              onBlur={() => {if(name && description && role) handleSubmit()}}
              invalid={name?false:true}
              message={!name && "Project name is required"}
              disabled={loading}
              />
          </Grid>
      </Grid>
      <Grid container item direction="column" xs={10}>
        <Input
          className={classes.input}
          textarea={true}
          variant="h1"
          rows={6}
          value={description}
          onChange={({ target }) => setDescription(target.value)}
          onBlur={() => {if(name && description && role) handleSubmit()}}
          invalid={description?false:true}
          message={!description && "Project description is required"}
          disabled={loading}
        />
        <Paper className={classes.container}>
        <Typography  variant="button" component="h4">
          Add Skill
        </Typography>
          <div className={classes.searcher}>
              <Searcher onSelect={(skill) => handleSelect(skill)} disabled={loading} />
          </div>
          <div className={classes.skills}>
            {skills.map((skill, index) => (
              <div key={`skill-${index}`} className={classes.skill}>
                <Skill {...skill} onDelete={() => handleDelete(skill)}/>
              </div>
            ))}
            </div>
          </Paper>
      </Grid>
      <Grid item xs={1}>
          <Button variant="outlined" color="primary" onClick={() => onDelete()} disabled={loading}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
      </Grid>
    </Grid>
  </>
  );
};

Edit.propTypes = {
  PROJECT,
};

export default Edit;
